<template>
  <template v-if="activeWorkspace?.id">
    <div
      v-if="workspaces.length === 1"
      :title="activeWorkspace.name"
      class="relative flex h-5 w-min max-w-full cursor-pointer items-center gap-2 rounded-[88px] text-xs font-semibold"
      :class="[
        fadedLabel ? 'text-gray-50 hover:bg-[--sidebar-link-background-hover]' : 'hover:bg-gray-20 text-gray-600',
        shadowLabel ? 'bg-white pl-2 pr-3 shadow-sm' : '-left-1 pl-1 pr-2',
      ]"
      data-testid="workspace-list-trigger"
    >
      <span
        class="shrink-0 rounded-full"
        :class="[
          fadedLabel ? 'border-subnav-background' : 'border-white',
          shadowLabel ? 'h-2 w-2' : 'h-3 w-3 border-2 shadow-sm',
        ]"
        :style="{ backgroundColor: activeWorkspace.color }"
      />
      <span class="max-w-[200px] truncate">{{ activeWorkspace.name }}</span>
    </div>
    <ui-dropdown
      v-else-if="workspaces"
      data-testid="workspace-selection"
      :model-value="[activeWorkspace.id]"
      :mode="Modes.Single"
      :options="options"
      :search-placeholder="$t('universal.search')"
      :no-results-text="$t('pickers.workspaces.no_results')"
      :has-search="workspaces.length > 8"
      prop-to-check="id"
      label="label"
      close-on-select
      filter-results
      :max-width="480"
      @update:model-value="$emit('switch-workspace', $event)"
    >
      <template #trigger="{ selectedOptions }">
        <ui-menu-base-trigger>
          <template #default="{ openMenu }">
            <div
              :title="activeWorkspace.name"
              class="relative flex h-5 w-min max-w-full cursor-pointer items-center gap-2 rounded-[88px] text-xs font-semibold"
              :class="[
                fadedLabel ? 'text-gray-50 hover:bg-[--sidebar-link-background-hover]' : 'hover:bg-gray-20',
                shadowLabel ? 'bg-white pl-2 pr-3 shadow-sm' : '-left-1 pl-1 pr-2',
              ]"
              data-testid="workspace-list-trigger"
              type="button"
              @click="openMenu"
            >
              <span
                class="shrink-0 rounded-full"
                :class="[
                  fadedLabel ? 'border-subnav-background' : 'border-white',
                  shadowLabel ? 'h-2 w-2' : 'h-3 w-3 border-2 shadow-sm',
                ]"
                :style="{ backgroundColor: selectedOptions[0]?.color }"
              />
              <span class="max-w-[200px] truncate">{{ selectedOptions[0]?.label }}</span>
            </div>
          </template>
        </ui-menu-base-trigger>
      </template>
      <template #option="{ option }">
        <div class="flex items-center gap-2" :title="option.label" data-testid="workspace-list-item">
          <span
            class="h-3 w-3 shrink-0 rounded-full border-2 border-white shadow-sm"
            :class="[fadedLabel && 'bg-border-subnav-background']"
            :style="{ backgroundColor: option.color }"
          />
          <span class="truncate">
            {{ option.label }}
          </span>
        </div>
      </template>
    </ui-dropdown>
  </template>
</template>

<script setup lang="ts" generic="T extends SessionWorkspace | WorkspaceItem">
import { UiDropdownV2 as UiDropdown, UiMenuBaseTrigger } from '@klausapp/ui-kit'
import { Modes } from '@klausapp/ui-kit/components/UiDropdownV2/types'
import { ref, computed } from 'vue'
import { useWorkspaceList } from '@/composables/useWorkspaceList'
import type { SessionWorkspace } from '@/types/session'
import type { WorkspaceItem } from '@/types/workspace'

defineProps<{
  fadedLabel?: boolean
  shadowLabel?: boolean
  activeWorkspace: T
}>()

defineEmits<{
  'switch-workspace': [workspaceId: number]
}>()

const tippyRef = ref<any>()

const [workspaces] = useWorkspaceList()

const options = computed(() => {
  return workspaces.value.map((workspace) => ({
    id: workspace.id,
    label: workspace.name,
    value: workspace.id,
    color: workspace.color,
  }))
})

defineExpose({
  tippyRef,
})
</script>
